import React, { useState, useEffect, useRef } from 'react';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Question } from "@/types/theme";
import { Info } from "lucide-react";

interface ValidationSettingsProps {
  question: Question;
  onUpdate: (updatedQuestion: Question) => void;
}

export const ValidationSettings: React.FC<ValidationSettingsProps> = ({
  question,
  onUpdate
}) => {
  // Debug logging removed to prevent console spam

  // Determine if validation is enabled for this question
  const isValidationEnabled = question.validationType !== 'none';

  // State for showing success message
  const [showSuccess, setShowSuccess] = useState(false);

  // State for tracking slider values during dragging
  const [sliderValue, setSliderValue] = useState<number>(question.validation_rules?.confidenceThreshold || 0.7);
  const [maxAttemptsValue, setMaxAttemptsValue] = useState<number>(question.validation_rules?.maxAttempts || 3);

  // State for tracking input values
  const [errorMessage, setErrorMessage] = useState<string>(question.errormessage || '');
  const [patternValue, setPatternValue] = useState<string>(question.validation_rules?.pattern || '');
  const [minValue, setMinValue] = useState<string>(question.validation_rules?.min?.toString() || '');
  const [maxValue, setMaxValue] = useState<string>(question.validation_rules?.max?.toString() || '');

  // Update state values when question changes
  useEffect(() => {
    setSliderValue(question.validation_rules?.confidenceThreshold || 0.7);
    setMaxAttemptsValue(question.validation_rules?.maxAttempts || 3);
    setErrorMessage(question.errormessage || '');
    setPatternValue(question.validation_rules?.pattern || '');
    setMinValue(question.validation_rules?.min?.toString() || '');
    setMaxValue(question.validation_rules?.max?.toString() || '');
  }, [question.id]); // Only depend on question.id to avoid infinite loops

  // Function to show success message temporarily
  const showSuccessMessage = () => {
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  // Handle validation type change
  const handleValidationTypeChange = (value: 'none' | 'basic' | 'ai') => {
    const updatedQuestion = {
      ...question,
      validationType: value
    };

    onUpdate(updatedQuestion);
    showSuccessMessage();
  };

  // Handle validation rules changes
  const handleValidationRuleChange = (key: string, value: any) => {
    const updatedRules = {
      ...question.validation_rules,
      [key]: value
    };

    onUpdate({
      ...question,
      validation_rules: updatedRules
    });

    showSuccessMessage();
  };

  // Handle error message change
  const handleErrorMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage(e.target.value);
  };

  // Handle error message blur
  const handleErrorMessageBlur = () => {
    if (errorMessage !== question.errormessage) {
      onUpdate({
        ...question,
        errormessage: errorMessage
      });
      showSuccessMessage();
    }
  };

  // Handle pattern change
  const handlePatternChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatternValue(e.target.value);
  };

  // Handle pattern blur
  const handlePatternBlur = () => {
    if (patternValue !== question.validation_rules?.pattern) {
      handleValidationRuleChange('pattern', patternValue);
    }
  };

  // Handle min value change
  const handleMinValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMinValue(e.target.value);
  };

  // Handle min value blur
  const handleMinValueBlur = () => {
    const numValue = parseFloat(minValue);
    if (!isNaN(numValue) && numValue !== question.validation_rules?.min) {
      handleValidationRuleChange('min', numValue);
    }
  };

  // Handle max value change
  const handleMaxValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxValue(e.target.value);
  };

  // Handle max value blur
  const handleMaxValueBlur = () => {
    const numValue = parseFloat(maxValue);
    if (!isNaN(numValue) && numValue !== question.validation_rules?.max) {
      handleValidationRuleChange('max', numValue);
    }
  };

  // Handle key down for inputs
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  // Validation toggle handlers
  const handleValidationToggle = (enabled: boolean) => {
    onUpdate({
      ...question,
      validationType: enabled ? 'basic' : 'none'
    });

    showSuccessMessage();
  };

  // Determine if AI validation is enabled
  const isAIValidation = question.validationType === 'ai';

  // Handle AI validation toggle
  const handleAIValidationToggle = (enabled: boolean) => {
    onUpdate({
      ...question,
      validationType: enabled ? 'ai' : 'basic'
    });

    showSuccessMessage();
  };

  // Determine if this question type supports validation
  const supportsValidation = !['button', 'select', 'no_response'].includes(question.type);

  if (!supportsValidation) {
    return (
      <div className="bg-gray-100 p-3 rounded-md mt-3">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm text-gray-700">
              Validation is not available for {question.type} questions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 p-3 rounded-md mt-3">
      <div className="space-y-4">
        {showSuccess && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded relative mb-2">
            <span className="block sm:inline">Validation settings saved successfully!</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor={`validation-toggle-${question.id}`}>Enable Validation</Label>
            <p className="text-xs text-gray-600">
              Verify user responses match the question's requirements
            </p>
          </div>
          <Switch
            id={`validation-toggle-${question.id}`}
            checked={isValidationEnabled}
            onCheckedChange={handleValidationToggle}
          />
        </div>

        {isValidationEnabled && (
          <>
            <div className="space-y-2">
              <Label htmlFor={`validation-type-${question.id}`}>Validation Type</Label>
              <Select
                value={question.validationType}
                onValueChange={(value: any) => handleValidationTypeChange(value)}
              >
                <SelectTrigger id={`validation-type-${question.id}`}>
                  <SelectValue placeholder="Select validation type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic (Pattern/Rules)</SelectItem>
                  <SelectItem value="ai">AI-Powered</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-600 mt-1">
                {question.validationType === 'basic'
                  ? 'Basic validation uses simple rules like required fields and patterns'
                  : 'AI validation uses artificial intelligence to verify responses are appropriate'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`error-message-${question.id}`}>Error Message</Label>
              <input
                id={`error-message-${question.id}`}
                type="text"
                value={errorMessage}
                onChange={handleErrorMessageChange}
                onBlur={handleErrorMessageBlur}
                onKeyDown={handleKeyDown}
                className="w-full p-2 border rounded-md"
                placeholder="Error message (shown when validation fails)"
              />
              <p className="text-xs text-gray-600 mt-1">
                This message will be shown to the user when their response fails validation
              </p>
            </div>

            {question.validationType === 'basic' && (
              <div className="space-y-3 pl-3 border-l-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor={`required-toggle-${question.id}`}>Required</Label>
                    <p className="text-xs text-gray-600">User must provide a response</p>
                  </div>
                  <Switch
                    id={`required-toggle-${question.id}`}
                    checked={question.validation_rules?.required || false}
                    onCheckedChange={(checked) => handleValidationRuleChange('required', checked)}
                  />
                </div>

                {question.type === 'text' && (
                  <div className="space-y-2">
                    <Label htmlFor={`pattern-${question.id}`}>Pattern (Regex)</Label>
                    <input
                      id={`pattern-${question.id}`}
                      type="text"
                      value={patternValue}
                      onChange={handlePatternChange}
                      onBlur={handlePatternBlur}
                      onKeyDown={handleKeyDown}
                      className="w-full p-2 border rounded-md text-sm"
                      placeholder="e.g., ^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
                    />
                    <p className="text-xs text-gray-600">
                      Regular expression pattern for validating text input
                    </p>
                  </div>
                )}

                {question.type === 'number' && (
                  <>
                    <div className="space-y-2">
                      <Label>Minimum Value</Label>
                      <input
                        type="number"
                        value={minValue}
                        onChange={handleMinValueChange}
                        onBlur={handleMinValueBlur}
                        onKeyDown={handleKeyDown}
                        className="w-full p-2 border rounded-md text-sm"
                        placeholder="Minimum value"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Maximum Value</Label>
                      <input
                        type="number"
                        value={maxValue}
                        onChange={handleMaxValueChange}
                        onBlur={handleMaxValueBlur}
                        onKeyDown={handleKeyDown}
                        className="w-full p-2 border rounded-md text-sm"
                        placeholder="Maximum value"
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <div className="space-y-0.5">
                    <Label>Max Clarification Attempts</Label>
                    <p className="text-xs text-gray-600">
                      Maximum follow-ups before accepting any response: {maxAttemptsValue}
                    </p>
                  </div>
                  <Slider
                    value={[maxAttemptsValue]}
                    min={1}
                    max={5}
                    step={1}
                    onValueChange={(value) => {
                      // Update local state immediately for smooth UI feedback
                      setMaxAttemptsValue(value[0]);
                    }}
                    onValueCommit={(value) => {
                      // Only save when the user releases the slider
                      handleValidationRuleChange('maxAttempts', value[0]);
                    }}
                  />
                </div>
              </div>
            )}

            {question.validationType === 'ai' && (
              <div className="space-y-3 pl-3 border-l-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor={`strict-toggle-${question.id}`}>Strict Validation</Label>
                    <p className="text-xs text-gray-600">
                      Require more precise responses (may reduce completion rates)
                    </p>
                  </div>
                  <Switch
                    id={`strict-toggle-${question.id}`}
                    checked={question.validation_rules?.strictValidation || false}
                    onCheckedChange={(checked) => handleValidationRuleChange('strictValidation', checked)}
                  />
                </div>

                <div className="space-y-2">
                  <div className="space-y-0.5">
                    <Label>Confidence Threshold</Label>
                    <p className="text-xs text-gray-600">
                      Minimum confidence score required to accept a response:
                      {sliderValue.toFixed(1)}
                    </p>
                  </div>
                  <Slider
                    value={[sliderValue]}
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    onValueChange={(value) => {
                      // Update local state immediately for smooth UI feedback
                      setSliderValue(value[0]);
                    }}
                    onValueCommit={(value) => {
                      // Only save when the user releases the slider
                      handleValidationRuleChange('confidenceThreshold', value[0]);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <div className="space-y-0.5">
                    <Label>Max Clarification Attempts</Label>
                    <p className="text-xs text-gray-600">
                      Maximum follow-ups before accepting any response: {maxAttemptsValue}
                    </p>
                  </div>
                  <Slider
                    value={[maxAttemptsValue]}
                    min={1}
                    max={5}
                    step={1}
                    onValueChange={(value) => {
                      // Update local state immediately for smooth UI feedback
                      setMaxAttemptsValue(value[0]);
                    }}
                    onValueCommit={(value) => {
                      // Only save when the user releases the slider
                      handleValidationRuleChange('maxAttempts', value[0]);
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
